// frontend/src/pages/Admin/ProspectDashboard.tsx
// Dashboard de prospection commerciale — SmartLawyer Admin

import { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Prospect {
  id:            string;
  name:          string;
  email:         string;
  firm_name:     string;
  firm_url:      string;
  city:          string;
  domain:        string;
  target_type:   string;
  source:        string;
  status:        'pending' | 'contacted' | 'skipped';
  email_subject: string;
  email_body:    string;
  found_at:      string;
  found_date:    string;
  contacted_at:  string | null;
  contacted_by:  string | null;
  sent_by:       string | null;
  notes:         string | null;
  siret:         string;
  phone?:        string;
  barreau?:      string;
  contact_evidence?: {
    emails: { value: string; source_url: string }[];
    phones: { value: string; source_url: string }[];
    pages:  string[];
  } | null;
}

interface Stats {
  total:        number;
  pending:      number;
  contacted:    number;
  skipped:      number;
  with_email:   number;
  by_type:      Record<string, number>;
  by_source:    Record<string, number>;
  taux_contact: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(iso));
  } catch { return iso; }
};

const TARGET_LABELS: Record<string, { label: string; color: string }> = {
  avocat_independant:  { label: 'Avocat indép.',    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  cabinet_avocats:     { label: 'Cabinet',          color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  departement_rh:      { label: 'Dép. RH',          color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  direction_juridique: { label: 'Dir. juridique',   color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:   { label: 'À contacter',  color: 'bg-muted text-muted-foreground' },
  contacted: { label: 'Contacté ✓',  color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  skipped:   { label: 'Ignoré',       color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400' },
};

const TypeBadge = ({ type }: { type: string }) => {
  const meta = TARGET_LABELS[type] || { label: type, color: 'bg-muted text-muted-foreground' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const meta = STATUS_LABELS[status] || { label: status, color: 'bg-muted text-muted-foreground' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) => (
  <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-2xl font-bold ${color || 'text-foreground'}`}>{value}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </div>
);

// ── Expéditeur ─────────────────────────────────────────────────────────────────

export interface SendOptions {
  sender_email: string;
  sender_name:  string;
  subject:      string;
  body:         string;
}

const SENDER_KEY = 'sl_prospect_sender';
const KNOWN_NAMES: Record<string, string> = {
  'pierrelouis@smartlawyer.ai': 'Pierre-Louis',
  'jeremie@smartlawyer.ai':     'Jérémie Lenoir',
};
const defaultName = (email: string) =>
  KNOWN_NAMES[email] ?? email.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Le modèle signe « Pierre-Louis / pierrelouis@… » : on l'adapte à l'expéditeur choisi
const adaptSignature = (body: string, name: string, email: string) =>
  body.replace(/Pierre-Louis/g, name).replace(/pierrelouis@smartlawyer\.ai/g, email);

// ── Modal Prévisualisation Email ───────────────────────────────────────────────

const EmailPreviewModal = ({
  prospect,
  senders,
  onClose,
  onSend,
  sending,
}: {
  prospect: Prospect;
  senders: string[];
  onClose: () => void;
  onSend: (prospect: Prospect, opts: SendOptions) => void;
  sending: boolean;
}) => {
  const saved = (() => { try { return JSON.parse(localStorage.getItem(SENDER_KEY) || '{}'); } catch { return {}; } })();
  const [senderEmail, setSenderEmail] = useState<string>(
    senders.includes(saved.email) ? saved.email : senders[0] || ''
  );
  const [senderName, setSenderName] = useState<string>(saved.email === senderEmail && saved.name ? saved.name : defaultName(senderEmail));
  const [subject, setSubject] = useState(prospect.email_subject || '');
  const [body, setBody] = useState(adaptSignature(prospect.email_body || '', senderName, senderEmail));
  const [mode, setMode] = useState<'texte' | 'apercu'>('texte');
  const [previewHtml, setPreviewHtml] = useState('');
  const apiClient = useContext(ChainlitContext);

  // Rendu HTML exact (logo, lien SmartLawyer, signature) calculé par le backend
  useEffect(() => {
    if (mode !== 'apercu') return;
    setPreviewHtml('');
    fetch(apiClient.buildEndpoint('/api/prospects/preview-email'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, sender_name: senderName, sender_email: senderEmail }),
    })
      .then(r => r.json())
      .then(d => setPreviewHtml(d.html || ''))
      .catch(() => setPreviewHtml('<p>Aperçu indisponible</p>'));
  }, [mode, body, senderName, senderEmail, apiClient]);

  const changeSender = (email: string) => {
    const name = defaultName(email);
    setBody(b => {
      // 1re sélection : adapter la signature du modèle ; ensuite : remplacer l'ancien expéditeur
      const withName = senderName ? b.split(senderName).join(name) : b;
      return senderEmail ? withName.split(senderEmail).join(email) : adaptSignature(withName, name, email);
    });
    setSenderEmail(email);
    setSenderName(name);
  };

  // Liste des expéditeurs arrivée après l'ouverture : prendre le premier
  useEffect(() => {
    if (!senderEmail && senders[0]) changeSender(senders[0]);
  }, [senders]);

  const send = () => {
    try { localStorage.setItem(SENDER_KEY, JSON.stringify({ email: senderEmail, name: senderName })); } catch { /* stockage indisponible */ }
    onSend(prospect, { sender_email: senderEmail, sender_name: senderName, subject, body });
  };

  return (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4">
    <div className="bg-background border border-border rounded-xl w-full max-w-2xl my-8 shadow-2xl">
      {/* Header */}
      <div className="flex items-start justify-between p-6 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Prévisualisation de l'email</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            À : {prospect.name}
            {prospect.email ? ` <${prospect.email}>` : ' — pas d\'email'}
          </p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none ml-4 flex-shrink-0">✕</button>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4">
        {/* Info prospect */}
        <div className="flex flex-wrap gap-2">
          <TypeBadge type={prospect.target_type} />
          <StatusBadge status={prospect.status} />
          {prospect.city && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{prospect.city}</span>
          )}
          {prospect.domain && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{prospect.domain}</span>
          )}
        </div>

        {/* Expéditeur */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Envoyer depuis</span>
            <select
              value={senderEmail}
              onChange={e => changeSender(e.target.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground"
            >
              {senders.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Nom affiché</span>
            <input
              value={senderName}
              onChange={e => {
                const next = e.target.value;
                // Signature mise à jour tant que l'ancien nom est non vide (split('') découperait tout)
                if (senderName && next) setBody(b => b.split(senderName).join(next));
                setSenderName(next);
              }}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground"
            />
          </label>
        </div>

        {/* Sujet */}
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Sujet</span>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            className="text-sm font-medium border border-border rounded-lg px-3 py-2 bg-background text-foreground"
          />
        </label>

        {/* Corps : édition du texte ou aperçu du rendu HTML réel */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Corps de l'email</span>
            <div className="flex text-xs border border-border rounded-lg overflow-hidden">
              {(['texte', 'apercu'] as const).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`px-3 py-1 ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  {m === 'texte' ? 'Texte' : 'Aperçu'}
                </button>
              ))}
            </div>
          </div>
          {mode === 'texte' ? (
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={14}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground leading-relaxed font-sans"
            />
          ) : (
            <iframe
              title="Aperçu de l'email"
              srcDoc={previewHtml || '<p style="font-family:sans-serif;color:#888">Chargement…</p>'}
              sandbox=""
              className="w-full h-[420px] rounded-lg border border-border bg-white"
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t border-border">
        <button
          onClick={onClose}
          className="text-sm px-4 py-2 rounded-lg border border-border hover:bg-muted text-muted-foreground"
        >
          Fermer
        </button>
        {prospect.status !== 'contacted' && (
          <button
            onClick={send}
            disabled={!prospect.email || !senderEmail || sending}
            title={!prospect.email ? "Pas d'adresse email" : undefined}
            className="text-sm px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {sending ? '…envoi…' : '✉ Envoyer maintenant'}
          </button>
        )}
      </div>
    </div>
  </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const ProspectDashboardContent = () => {
  const { isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate  = useNavigate();

  const [stats,       setStats]       = useState<Stats | null>(null);
  const [prospects,   setProspects]   = useState<Prospect[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [loadingStats,setLoadingStats]= useState(true);
  const [error,       setError]       = useState('');
  const [preview,     setPreview]     = useState<Prospect | null>(null);
  const [sending,     setSending]     = useState(false);
  const [actionMsg,   setActionMsg]   = useState('');

  // Filtres
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterType,    setFilterType]    = useState('');
  const [filterCity,    setFilterCity]    = useState('');
  const [filterSearch,  setFilterSearch]  = useState('');
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  // Stats
  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    setLoadingStats(true);
    fetch(apiClient.buildEndpoint('/api/prospects/stats'))
      .then(r => r.json())
      .then(d => { setStats(d); setLoadingStats(false); })
      .catch(() => setLoadingStats(false));
  }, [isAuthenticated, isReady, apiClient]);

  // Liste
  const loadProspects = useCallback(() => {
    if (!isAuthenticated || !isReady) return;
    setLoading(true);
    setError('');

    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (filterStatus) params.set('status', filterStatus);
    if (filterType)   params.set('target_type', filterType);
    if (filterCity)   params.set('city', filterCity);
    if (filterSearch) params.set('search', filterSearch);

    fetch(apiClient.buildEndpoint(`/api/prospects/?${params}`))
      .then(r => { if (!r.ok) throw new Error('Erreur'); return r.json(); })
      .then(d => {
        setProspects(d.items ?? []);
        setTotal(d.total ?? 0);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [isAuthenticated, isReady, apiClient, page, filterStatus, filterType, filterCity, filterSearch]);

  useEffect(() => { loadProspects(); }, [loadProspects]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Actions ──────────────────────────────────────────────────────────────────

  // Comptes d'envoi configurés (GMAIL_EMAIL_ADDRESS + GMAIL_SENDERS)
  const [senders, setSenders] = useState<string[]>([]);
  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    fetch(apiClient.buildEndpoint('/api/users/broadcast-senders'), { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { senders: [] }))
      .then(d => setSenders(d.senders ?? []))
      .catch(() => setSenders([]));
  }, [isAuthenticated, isReady, apiClient]);

  const handleSend = async (prospect: Prospect, opts: SendOptions) => {
    if (!prospect.email) return;
    setSending(true);
    try {
      const resp = await fetch(
        apiClient.buildEndpoint(`/api/prospects/${prospect.id}/send-email`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(opts),
        }
      );
      if (resp.ok) {
        setActionMsg(`✓ Email mis en file pour ${prospect.email} (depuis ${opts.sender_email})`);
        setPreview(null);
        loadProspects();
        // Recharger les stats
        fetch(apiClient.buildEndpoint('/api/prospects/stats')).then(r => r.json()).then(setStats);
      } else {
        const d = await resp.json();
        setActionMsg(`✗ Erreur : ${d.detail || 'Envoi échoué'}`);
      }
    } catch {
      setActionMsg('✗ Erreur réseau');
    } finally {
      setSending(false);
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  const handleSkip = async (id: string) => {
    await fetch(apiClient.buildEndpoint(`/api/prospects/${id}/skip`), { method: 'POST' });
    loadProspects();
    fetch(apiClient.buildEndpoint('/api/prospects/stats')).then(r => r.json()).then(setStats);
  };

  const _handleMarkContacted = async (id: string) => {
    await fetch(apiClient.buildEndpoint(`/api/prospects/${id}/contacted`), { method: 'POST' });
    loadProspects();
    fetch(apiClient.buildEndpoint('/api/prospects/stats')).then(r => r.json()).then(setStats);
  };

  // ── FilterSelect ─────────────────────────────────────────────────────────────

  const FilterSelect = ({ label, value, onChange, options }: {
    label: string; value: string; onChange: (v: string) => void;
    options: { value: string; label: string }[];
  }) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={e => { onChange(e.target.value); setPage(1); }}
        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  // ── Rendu ─────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto w-full h-full overflow-y-auto [&>*]:shrink-0">

      {/* Titre */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Prospection</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gestion des prospects commerciaux — avocats, cabinets, RH & directions juridiques
        </p>
      </div>

      {/* Message d'action */}
      {actionMsg && (
        <div className={`text-sm px-4 py-2 rounded-lg ${actionMsg.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
          {actionMsg}
        </div>
      )}

      {/* Stats */}
      {loadingStats
        ? <div className="flex gap-2"><Loader /></div>
        : stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard label="Total trouvés"     value={stats.total}      />
            <StatCard label="À contacter"       value={stats.pending}    color="text-blue-600" />
            <StatCard label="Contactés"         value={stats.contacted}  color="text-emerald-600" sub={`${stats.taux_contact}% du total`} />
            <StatCard label="Ignorés"           value={stats.skipped}    color="text-rose-500" />
            <StatCard label="Avec email"        value={stats.with_email} color="text-violet-600" sub={`${stats.total ? Math.round(stats.with_email/stats.total*100) : 0}% emailables`} />
            <StatCard
              label="Avocats / RH"
              value={`${stats.by_type?.avocat_independant ?? 0} + ${stats.by_type?.cabinet_avocats ?? 0}`}
              sub={`${stats.by_type?.departement_rh ?? 0} RH · ${stats.by_type?.direction_juridique ?? 0} Dir. jur.`}
            />
          </div>
        )}

      {/* Barre de progression */}
      {stats && stats.total > 0 && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Taux de contact</span>
              <span>{stats.contacted}/{stats.total}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${stats.taux_contact}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap gap-4 items-end">
        <FilterSelect
          label="Statut"
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: '', label: 'Tous' },
            { value: 'pending',   label: 'À contacter' },
            { value: 'contacted', label: 'Contactés' },
            { value: 'skipped',   label: 'Ignorés' },
          ]}
        />
        <FilterSelect
          label="Type de cible"
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: '', label: 'Tous' },
            { value: 'avocat_independant',  label: 'Avocat indépendant' },
            { value: 'cabinet_avocats',     label: 'Cabinet d\'avocats' },
            { value: 'departement_rh',      label: 'Département RH' },
            { value: 'direction_juridique', label: 'Direction juridique' },
          ]}
        />
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Recherche</label>
          <input
            type="text"
            value={filterSearch}
            onChange={e => { setFilterSearch(e.target.value); setPage(1); }}
            placeholder="Nom, email, cabinet, ville…"
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          onClick={() => { setFilterStatus(''); setFilterType(''); setFilterCity(''); setFilterSearch(''); setPage(1); }}
          className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 border border-border rounded-lg"
        >
          Réinitialiser
        </button>
      </div>

      {/* Tableau */}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {loading
        ? <div className="flex justify-center py-12"><Loader /></div>
        : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm text-muted-foreground">
              {total} prospect{total > 1 ? 's' : ''} · page {page}/{totalPages || 1}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Contact</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">Type</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">Domaine</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">Email</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Source</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground">Statut</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">Trouvé le</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map(p => (
                    <tr key={p.id} className="border-b border-border hover:bg-muted/20 transition-colors">
                      {/* Contact */}
                      <td className="px-4 py-3 max-w-xs">
                        <div className="font-medium text-foreground truncate">{p.name || '—'}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.firm_name || '—'}
                          {p.city ? ` · ${p.city}` : ''}
                        </div>
                        {p.firm_url && (
                          <a
                            href={p.firm_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline truncate block"
                          >
                            {p.firm_url.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-3 py-3">
                        <TypeBadge type={p.target_type} />
                      </td>

                      {/* Domaine */}
                      <td className="px-3 py-3 text-xs text-muted-foreground max-w-[140px]">
                        <span className="truncate block">{p.domain || '—'}</span>
                      </td>

                      {/* Email */}
                      <td className="px-3 py-3 text-xs">
                        {p.email
                          ? <a href={`mailto:${p.email}`} className="text-blue-500 hover:underline">{p.email}</a>
                          : <span className="text-muted-foreground">—</span>
                        }
                        {p.phone && (
                          <div className="mt-0.5">
                            <a href={`tel:${p.phone.replace(/\s/g, '')}`} className="text-muted-foreground hover:underline">☎ {p.phone}</a>
                          </div>
                        )}
                        {p.contact_evidence?.pages?.[0] && (
                          <a
                            href={p.contact_evidence.pages[0]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mt-0.5 text-[10px] text-muted-foreground hover:underline truncate max-w-[200px]"
                            title="Page où le contact a été trouvé"
                          >
                            source : {p.contact_evidence.pages[0].replace(/^https?:\/\/(www\.)?/, '')}
                          </a>
                        )}
                      </td>

                      {/* Source */}
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                          {p.source || '—'}
                        </span>
                      </td>

                      {/* Statut */}
                      <td className="px-3 py-3 text-center">
                        <StatusBadge status={p.status} />
                        {p.contacted_by && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            par {p.contacted_by}
                          </div>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {fmtDate(p.found_date || p.found_at)}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex gap-1.5 justify-end">
                          <button
                            onClick={() => setPreview(p)}
                            className="text-xs px-2.5 py-1 rounded-lg bg-muted hover:bg-muted/70 text-foreground transition-colors"
                            title="Voir l'email d'accroche"
                          >
                            ✉ Email
                          </button>
                          {p.status === 'pending' && (
                            <>
                              {p.email && (
                                <button
                                  onClick={() => setPreview(p)}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                                  title="Envoyer l'email"
                                >
                                  ↗
                                </button>
                              )}
                              <button
                                onClick={() => handleSkip(p.id)}
                                className="text-xs px-2.5 py-1 rounded-lg bg-muted hover:bg-rose-50 text-rose-500 border border-transparent hover:border-rose-200 transition-colors"
                                title="Ignorer ce prospect"
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {prospects.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-muted-foreground">
                        {total === 0
                          ? 'Aucun prospect — lancez le cron prospect_finder.py pour en trouver.'
                          : 'Aucun prospect correspondant aux filtres.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 p-4 border-t border-border">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  ← Précédent
                </button>
                <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-sm px-3 py-1.5 rounded border border-border disabled:opacity-40 hover:bg-muted"
                >
                  Suivant →
                </button>
              </div>
            )}
          </div>
        )}

      {/* Modal prévisualisation email */}
      {preview && (
        <EmailPreviewModal
          prospect={preview}
          onClose={() => setPreview(null)}
          senders={senders}
          onSend={handleSend}
          sending={sending}
        />
      )}
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

const ProspectDashboard = () => (
  <Page>
    <ProspectDashboardContent />
  </Page>
);

export default ProspectDashboard;
